import { useState, useEffect, useRef, useCallback } from 'react';
import type { FC } from 'react';
import { Input } from '@ui';
import { notifications } from '@utils/notifications';
import { createAddressString } from '@utils/address';
import { observer } from 'mobx-react';
import { when } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import DeliveryAddressModal from '@components/AddressSelection/DeliveryAddressModal';
import preparePoint from '@utils/preparePoint';
import useAddressForm from '@customHooks/useAddressForm';
import useAddressMap from '@customHooks/useAddressMap';
import useAddressSave from '@customHooks/useAddressSave';
import { useStores } from '@root/src/stores';

interface HeaderAddressModalProps {
  changeModalState: () => void;
  initialState?: any;
  defState?: any;
}

const HeaderAddressModal: FC<HeaderAddressModalProps> = observer(
  ({ changeModalState, initialState, defState }) => {
    const { cartStore, userStore } = useStores();
    const [editAddressState, setEditAddressState] = useState(false);

    const {
      fields,
      setFields,
      editAddress: setFormAddress,
      prepareNewAddress: initNewAddress,
      selectMainAddress
    } = useAddressForm();

    const {
      fio,
      phone,
      email,
      address,
      flat,
      comment,
      dadataId,
      coords,
      balloonState,
      isMainAddress,
      isAddingNewAddress,
      isShowMapMarkPlace,
      selectedAddress
    } = fields;

    const isAddingNewAddressRef = useRef(isAddingNewAddress);
    isAddingNewAddressRef.current = isAddingNewAddress;

    const {
      onSelectDaData,
      onMapClick,
      createTemplateLayoutFactory,
      isDefaultMark: isDefaultMarkByCoords,
      iconContentTemplate,
      mapRefs
    } = useAddressMap({
      setFields,
      onAfterAddressAccepted: ({ dadataId: did, source }) => {
        if (source === 'daData') {
          cartStore.updateCartStage({
            dadataId: Number(did),
            addressConfirmedByUser: false
          });
        } else if (
          source === 'balloon' &&
          !isAddingNewAddressRef.current &&
          did
        ) {
          cartStore.updateCartStage({
            dadataId: Number(did),
            addressConfirmedByUser: true
          });
        }
      },
      isEditMode: () => editAddressState
    });

    const isDefaultMark = useCallback(
      () => isDefaultMarkByCoords(coords),
      [isDefaultMarkByCoords, coords]
    );

    const handleAddressSelect = useCallback(
      (addr: any) => {
        if (cartStore.cart.peopleAddress?.id !== addr.id) {
          cartStore.updateCartStage({ peopleAddressId: addr.id });
        }
      },
      [cartStore]
    );

    const { setAddressComments, selectAddress: selectAddressFn } =
      useAddressSave({
        userStore,
        fields,
        setFields,
        onAfterSelect: handleAddressSelect
      });

    const editAddressFn = useCallback(
      (addr: any, e?: any) => {
        if (e) e.stopPropagation();
        setFormAddress(addr);
        setEditAddressState(true);
        cartStore.updateCartStage({ peopleAddressId: addr.id });
      },
      [setFormAddress, cartStore]
    );

    const prepareNewAddress = useCallback(() => {
      if (userStore.user) {
        initNewAddress(userStore.user);
      }
      setEditAddressState(true);
    }, [initNewAddress, userStore]);

    const removeAddress = useCallback(
      (addr: any, e?: any) => {
        if (e) e.stopPropagation();

        let removedId: string | number | undefined;
        if (addr) {
          removedId = addr.id;
        } else if (selectedAddress) {
          removedId = selectedAddress;
        } else {
          const primaryAddress = userStore.presortedAddresses?.find(
            (item: any) => item.primary
          );
          setEditAddressState(false);
          setFields({
            isAddingNewAddress: false,
            selectedAddress: primaryAddress?.id || undefined
          });
          return;
        }

        userStore.removeAddress(Number(removedId)).then((res: any) => {
          if (selectedAddress === addr?.id) {
            const foundAddr =
              res.addresses?.find((item: any) => item.primary) ||
              res.addresses[0];
            selectAddressFn(foundAddr);
          }
        });
      },
      [selectedAddress, userStore, selectAddressFn, setFields]
    );

    const isDisabledBtnSaveAddress = useCallback((state: any) => {
      const {
        selectedAddress: selAddr,
        phone: ph,
        dadata,
        balloonState: bs
      } = state;
      if (selAddr) {
        return !dadata || !isEmpty(bs);
      }
      return !dadata || !ph || (ph.includes('+7 (') && ph.length !== 18);
    }, []);

    const saveAddress = useCallback(
      async (dataNew: any, commentsData: any) => {
        const { fio: newFio, phone: newPhone, dadata } = dataNew;
        if (
          address &&
          !isDisabledBtnSaveAddress({
            selectedAddress,
            fio: newFio,
            phone: newPhone,
            dadata,
            address,
            balloonState
          }) &&
          ((newPhone.includes('+7 (') && newPhone.length === 18) ||
            (!newPhone.includes('+7 (') && newPhone.length === 10))
        ) {
          const { addPeopleAddress, updatePeopleAddress } = userStore;
          if (!isAddingNewAddress) {
            const res = await updatePeopleAddress({
              ...dataNew,
              id: selectedAddress
            });
            await setAddressComments(Number(selectedAddress), commentsData);
            if (res.updatePeopleAddress?.peopleAddress) {
              selectAddressFn(res.updatePeopleAddress.peopleAddress);
              // handleAddressSelect skips updateCartStage when the ID hasn't changed,
              // so force a cart refresh here to update cart.dadata in the header.
              await cartStore.updateCartStage({
                peopleAddressId: Number(selectedAddress)
              });
            }
          } else {
            const res = await addPeopleAddress(dataNew);
            const addr = res?.addPeopleAddress?.peopleAddress;
            if (!addr?.id) return;
            await setAddressComments(addr.id, commentsData);
            selectAddressFn(addr);
          }
        } else {
          notifications(
            'Проверьте правильность введенного номера телефона и адреса доставки!',
            'Внимание'
          );
        }
      },
      [
        address,
        balloonState,
        isAddingNewAddress,
        selectedAddress,
        userStore,
        cartStore,
        isDisabledBtnSaveAddress,
        setAddressComments,
        selectAddressFn
      ]
    );

    // Set initial state if provided
    useEffect(() => {
      if (initialState) {
        setFormAddress(initialState);
        setEditAddressState(true);
        cartStore.updateCartStage({ peopleAddressId: initialState.id });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialState, cartStore]);

    // MobX when: load cart/user data into form
    useEffect(() => {
      const dispose1 = when(
        () => {
          const { isLoadCart } = cartStore;
          const { isLoad, user } = userStore;
          return isLoadCart && isLoad && isEmpty(user);
        },
        () => {
          const { cart } = cartStore;
          setFields({
            address: createAddressString({ dadata: cart.dadata }),
            fio: cart.fio || '',
            phone: cart.phone || '',
            email: (cart as any).email || '',
            flat: cart.flat || ''
          });
        }
      );

      const dispose2 = when(
        () => {
          const { isLoadCart } = cartStore;
          const { isLoad, user } = userStore;
          return isLoad && isLoadCart && !isEmpty(user);
        },
        () => {
          const { presortedAddresses } = userStore;
          const { cart } = cartStore;
          const selAddr = cart.peopleAddress ? cart.peopleAddress.id : '';
          if (
            selAddr &&
            !defState &&
            !initialState &&
            !cartStore.isCartAddressFromOtherRegion
          ) {
            const currentAddress = presortedAddresses?.find(
              (i: any) => i.id === selAddr
            );
            if (currentAddress) selectAddressFn(currentAddress);
          }
        }
      );

      return () => {
        dispose1();
        dispose2();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectAddressFn]);

    const onFocus = useCallback(() => {}, []);
    const onBlur = useCallback(() => {}, []);

    const onInputBlur = useCallback(
      (...data: any[]) => {
        let [value, field] = data;
        if (field === 'comment' && value === '') value = ' ';
        if (!isAddingNewAddress) cartStore.updateCartStage({ [field]: value });
        onBlur();
      },
      [isAddingNewAddress, cartStore, onBlur]
    );

    const cancelChanges = useCallback(() => {
      setEditAddressState(false);
      setFields({ isAddAddress: false, isAddingNewAddress: false });
    }, [setFields]);

    const isLoggedIn = userStore.isUserLoggedIn;

    return (
      <DeliveryAddressModal
        setFields={setFields}
        showMobileMap={() => null}
        setShowModal={changeModalState}
        isMobileMapOpened={false}
        activeStore={false}
        coords={coords}
        preparePoint={preparePoint}
        selectedAddress={selectedAddress}
        selectAddress={selectAddressFn}
        removeAddress={removeAddress}
        fio={fio}
        phone={phone}
        comment={comment}
        flat={flat}
        isMainAddress={isMainAddress}
        selectMainAddress={selectMainAddress}
        dadataId={dadataId}
        address={address}
        defState={defState}
        mapRef={(ref: any) => {
          mapRefs.current.mapRefModal = ref;
        }}
        onMapClick={onMapClick}
        onLoadMap={createTemplateLayoutFactory}
        editAddress={editAddressState}
        iconContentTemplate={iconContentTemplate}
        isShowMapMarkPlace={isShowMapMarkPlace}
        isDefaultMark={isDefaultMark}
        saveAddress={saveAddress}
        prepareNewAddress={prepareNewAddress}
        cancelChanges={cancelChanges}
        onEditAddress={editAddressFn}
        initialState={initialState}
        addressComponent={(flatInput: any) => (
          <div className='address-info'>
            <Input
              classNameParent='order-page-input'
              classNameInput='custom-input'
              placeHolder='Начните вводить ваш адрес'
              value={address}
              field='address'
              onFocus={onFocus}
              onBlur={onBlur}
              inputType='text'
              onSelectDaData={onSelectDaData}
              isAddressError={!isEmpty(balloonState)}
              addressErrorText={
                !isEmpty(balloonState)
                  ? 'Укажите номер дома либо подтвердите на карте'
                  : ''
              }
            />
            {flatInput}
          </div>
        )}
        userComponent={(fioInput: any, phoneInput: any) => (
          <>
            {fioInput}
            {!isLoggedIn && (
              <Input
                classNameParent='order-page-input'
                classNameInput='custom-input'
                placeHolder='Электронная почта'
                value={email}
                field='email'
                onFocus={onFocus}
                onBlur={onBlur}
                onInputBlur={onInputBlur}
                inputType='text'
                autocomplete='email'
              />
            )}
            {phoneInput}
          </>
        )}
      />
    );
  }
);

export default HeaderAddressModal;
